import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { Constant } from '../ApiRules/ApiRules';

const tempMeeApi = axios.create({
  baseURL: Constant.BASE_URL,
  withCredentials: true,
});

tempMeeApi.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...{ 'x-correlation-id': uuid() },
    ...config.headers,
    'x-user-agent': navigator.userAgent,
  },
}));

let accessTokenProvider = null;

/**
 * Registers where requests get their access token. The session store
 * (`src/domain/Auth`) registers itself when it loads; it is the only thing that
 * knows the token. Inverted like this so this module imports nothing from the
 * session, and the session's own clients can import this one.
 *
 * @param {() => Promise<string>} provider
 */
export const setAccessTokenProvider = (provider) => {
  accessTokenProvider = provider;
};

/**
 * Every request carries the session's access token as a raw `Authorization`
 * header — the one scheme every backend behind the gateway accepts (the
 * monolith and awards-service refuse `Bearer`; auth-service takes either).
 *
 * A request that must go without a session — sign-in, the refresh itself,
 * revoking a session — says so with `skipAuth: true` in its config. When the
 * session has ended the request is refused before it leaves, with a
 * `SessionEndedError`.
 */
tempMeeApi.interceptors.request.use(async (config) => {
  if (config.skipAuth) return config;
  if (!accessTokenProvider) {
    throw new Error('No access token provider: import src/domain/Auth before sending requests');
  }
  const accessToken = await accessTokenProvider();
  return { ...config, headers: { ...config.headers, Authorization: accessToken } };
});

export const callAuthServiceApi = async (method, url, data, params) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  const response = await tempMeeApi({
    method,
    url: `/auth-service${normalizedUrl}`,
    data,
    params,
  });

  return response.data;
};

export default tempMeeApi;
